/**
 * CallDispoModal — post-call dispo capture (situational + linear).
 *
 * Shown ONLY after a call ends. Triggered from QuickResolveIssueModal's
 * one-tap "Open call dispo" path. Three linear, deterministic steps so
 * downstream automations have precise inputs:
 *
 *   Step 1 — Call outcome      (situational dropdown by IssueScenario + Other)
 *   Step 2 — Resolution direction (deterministic → IssueStatus)
 *   Step 3 — Optional notes     (free-text "Other" detail)
 *
 * Side effects on Confirm:
 *   • Appends a CustomerCommEntry (Phone Call) — same path as LogInteraction
 *   • Reports the chosen status + structured summary back to the caller
 *     so the Resolve modal can mark the issue accordingly.
 *
 * Distinct from LogInteractionModal (free-form, generic) — this one is
 * scenario-aware, linear, and lossless for automation.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Phone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import type {
  CustomerCommEntry,
  Issue,
  IssueStatus,
} from "@/data/types";
import {
  resolveIssueScenario,
} from "@/data/situational/requiredInputs";
import {
  RESOLUTION_DIRECTIONS,
  getCallOutcomeOptions,
  getDirectionByValue,
  getOutcomeLabel,
  type ResolutionDirection,
} from "@/data/situational/callDispoOptions";

export interface CallDispoResult {
  /** The resulting issue status after applying the chosen direction. */
  status: IssueStatus;
  /** Concise human-readable summary for the resolution / activity log. */
  summary: string;
  /** Raw structured payload — for downstream automation. */
  payload: {
    outcomeValue: string;
    outcomeLabel: string;
    direction: ResolutionDirection;
    directionLabel: string;
    otherText?: string;
    notes?: string;
  };
  /** The CustomerCommEntry that was appended. */
  entry: CustomerCommEntry;
}

export interface CallDispoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The issue the dispo is being captured for. Drives the situational menus. */
  issue: Issue;
  /** Customer name for the comm log entry. */
  customerName: string;
  /** Optional related job id for the comm log entry. */
  relatedJobId?: string;
  /** Fired only when the dispo is committed. */
  onLogged: (result: CallDispoResult) => void;
}

export function CallDispoModal({
  open,
  onOpenChange,
  issue,
  customerName,
  relatedJobId,
  onLogged,
}: CallDispoModalProps) {
  const { setCustomerComms } = useAppData();
  const { role } = useRole();

  const scenario = useMemo(() => resolveIssueScenario(issue), [issue]);
  const outcomeOptions = useMemo(() => getCallOutcomeOptions(scenario), [scenario]);

  const [outcome, setOutcome] = useState<string>(outcomeOptions[0]?.value ?? "other");
  const [otherText, setOtherText] = useState("");
  const [direction, setDirection] = useState<ResolutionDirection>("action_assigned");
  const [notes, setNotes] = useState("");

  // Reset on open + auto-suggest direction from chosen outcome.
  useEffect(() => {
    if (!open) return;
    const first = outcomeOptions[0]?.value ?? "other";
    setOutcome(first);
    setOtherText("");
    setNotes("");
    const suggested = outcomeOptions[0]?.suggestsDirection ?? "action_assigned";
    setDirection(suggested);
  }, [open, outcomeOptions]);

  // When the operator picks a different outcome, suggest the matching direction
  // (they can still override). Keeps Step 2 fast for the 80% case.
  const handleOutcomeChange = (value: string) => {
    setOutcome(value);
    const opt = outcomeOptions.find((o) => o.value === value);
    if (opt?.suggestsDirection) {
      setDirection(opt.suggestsDirection);
    }
  };

  const isOther = outcome === "other";
  const directionMeta = getDirectionByValue(direction);

  const handleConfirm = () => {
    if (isOther && !otherText.trim()) {
      toast.error("Describe the call outcome (Other selected)");
      return;
    }

    const outcomeLabel = isOther
      ? `Other — ${otherText.trim()}`
      : getOutcomeLabel(scenario, outcome);

    const summary = `${outcomeLabel} · ${directionMeta.label}${
      notes.trim() ? ` — ${notes.trim()}` : ""
    }`;

    const now = new Date();
    const entry: CustomerCommEntry = {
      id: `comm-${Date.now()}`,
      date: now.toISOString().slice(0, 10),
      time: now.toTimeString().slice(0, 5),
      commType: "Phone Call",
      summary,
      relatedJobId,
      user: role || "User",
      source: "Manual",
      origin: "manual",
    };
    setCustomerComms((prev) => [entry, ...(prev ?? [])]);

    onLogged({
      status: directionMeta.status,
      summary,
      payload: {
        outcomeValue: outcome,
        outcomeLabel,
        direction,
        directionLabel: directionMeta.label,
        otherText: isOther ? otherText.trim() : undefined,
        notes: notes.trim() || undefined,
      },
      entry,
    });
    toast.success(`Dispo logged — ${directionMeta.label}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-primary" /> Call Dispo
          </DialogTitle>
          <DialogDescription>
            {customerName} · {issue.id}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Step 1 — Call outcome (situational) */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                1
              </span>
              <Label className="text-xs font-semibold">What happened on the call?</Label>
            </div>
            <Select value={outcome} onValueChange={handleOutcomeChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {outcomeOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isOther && (
              <Input
                autoFocus
                placeholder="Describe what happened on the call"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                maxLength={300}
                className="mt-1.5"
              />
            )}
          </div>

          {/* Step 2 — Resolution direction (deterministic) */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                2
              </span>
              <Label className="text-xs font-semibold">Where does this go next?</Label>
            </div>
            <Select
              value={direction}
              onValueChange={(v) => setDirection(v as ResolutionDirection)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RESOLUTION_DIRECTIONS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              {directionMeta.helper}{" "}
              <span className="text-foreground font-medium">
                Will set status: {directionMeta.status}.
              </span>
            </p>
          </div>

          {/* Step 3 — Optional context */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-bold text-muted-foreground">
                3
              </span>
              <Label className="text-xs font-semibold">
                Anything else?{" "}
                <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
            </div>
            <Textarea
              placeholder="Add free-form context — only if the dropdowns above didn't capture it."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              className="resize-none text-xs"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} className="gap-1.5">
            <CheckCircle2 className="h-4 w-4" />
            Confirm Dispo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}